import { useState } from "react";
import { auth, db, functions } from "@firebase";
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  signOut,
  fetchSignInMethodsForEmail,
  linkWithCredential,
} from "firebase/auth";
import { ILoginFormType } from "../../../types";
import { UserService } from "services/UserService";
import axios from "axios";
import { collection, query, where, getDocs } from "firebase/firestore";

export const useAuthData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userNotRegistered, setUserNotRegistered] = useState<string | null>(
    null
  );

  // Register new user
  const onRegister = async ({ email, password }: ILoginFormType) => {
    setLoading(true);
    setError(null);

    try {
      console.log("email", email);
      console.log("password", password);
      // Create user with Firebase Authentication
      const response = await createUserWithEmailAndPassword(
        auth,
        email,
        password!
      );

      console.log("response", response);
      const { uid } = response.user;

      // Create user data
      const formData = {
        email: email,
        isAdmin: false,
        firstName: "",
        lastName: "",
        pronouns: "",
        type: "standard-user",
        biography: "",
        degrees: [],
        certifications: [],
        licenseExpiration: new Date(),
        licenseNumber: "",
        licenseImageUrl: "",
        insuranceNumber: "",
        insuranceExpiration: new Date(),
        insuranceImageUrl: "",
        createAt: new Date(),
        id: uid,
        isLicenseVerified: false,
        isInsuranceVerified: false,
        userStatus: "active",
        accountStatus: "approve",
      };

      // Call UserService.createUser with uid and formData
      await UserService.createUser(uid, formData);
      return response.user;
    } catch (error: any) {
      if (error.code === "auth/email-already-in-use") {
        setError("This Email address is already taken");
      } else {
        setError(error.message || "Registration failed");
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Login existing user
  const onLogin = async ({ email, password }: ILoginFormType) => {
    setLoading(true);
    setError(null);
    setUserNotRegistered(null);

    try {
      const response = await signInWithEmailAndPassword(auth, email, password!);
      return response.user;
    } catch (error: any) {
      setError(
        error.message || "An unexpected error occurred. Please try again."
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  // user signOut
  const onSignOut = async () => {
    setLoading(true);
    setError(null);
    setUserNotRegistered(null);
    try {
      const response = await signOut(auth);
      return response;
    } catch (error: any) {
      setError(error.message || "Signout failed");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: any) => {
    try {
      const result = await signInWithPopup(auth, provider);
      console.log("Login Successful:", result.user);
      return result.user;
    } catch (error: any) {
      if (error.code === "auth/account-exists-with-different-credential") {
        const email = error.customData?.email;
        const pendingCredential = error.credential;

        if (email) {
          const signInMethods = await fetchSignInMethodsForEmail(auth, email);

          if (signInMethods.length > 0) {
            // Inform the user about the existing account and prompt them to sign in with the existing provider
            alert(
              `An account already exists with the email ${email} associated with ${signInMethods[0]}. Please sign in using that provider to link your ${provider.providerId} account.`
            );

            // Example: If the existing provider is Google
            if (signInMethods[0] === "google.com") {
              const googleProvider = new GoogleAuthProvider();
              const googleSignInResult = await signInWithPopup(
                auth,
                googleProvider
              );
              // Link the pending credential (e.g., Facebook) to the existing user
              await linkWithCredential(
                googleSignInResult.user,
                pendingCredential
              );
              return googleSignInResult.user;
            }
            // Handle other providers accordingly
          }
        }
      } else {
        console.error("OAuth Login Error:", error);
      }

      if (error.code === "auth/popup-closed-by-user") {
        alert("Popup closed before login. Please try again.");
      } else if (error.code === "auth/popup-blocked") {
        alert("Popup was blocked. Please enable popups for this site.");
      }

      return null;
    }
  };

  // Send password reset email
  const sendPasswordReset = async (email: string) => {
    try {
      // First check if user exists
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const usersSnapshot = await getDocs(q);

      if (usersSnapshot.empty) {
        return {
          success: false,
          error: "No user found with this email address",
        };
      }

      // For valid users, send reset email
      console.log(window.location.origin);
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + "/login",
        handleCodeInApp: true,
      });

      return { success: true };
    } catch (error: any) {
      console.error("Password reset error:", error);
      return {
        success: false,
        error:
          error.code === "auth/invalid-email"
            ? "Invalid email address"
            : "Failed to send password reset email",
      };
    }
  };

  const confrimPasswordChange = async (code: string, newPassword: string) => {
    try {
      await confirmPasswordReset(auth, code, newPassword);
      return true;
    } catch (error: any) {
      setError(error.message || "Password reset failed");
      return false;
    }
  };

  const loginWithGoogle = () => handleOAuthLogin(new GoogleAuthProvider());

  const loginWithFacebook = () => {
    console.log("🟦 loginWithFacebook called...");
    return handleOAuthLogin(new FacebookAuthProvider()); // ✅ this should return a Promise<user>
  };

  // const loginWithTwitter = () => handleOAuthLogin(new TwitterAuthProvider());
  const loginWithTwitter = () =>
    handleOAuthLogin(new TwitterAuthProvider()).catch((error) =>
      console.error("Twitter Popup Blocked:", error)
    );

  return {
    loginWithGoogle,
    loginWithFacebook,
    loginWithTwitter,
    onLogin,
    onSignOut,
    onRegister,
    sendPasswordReset,
    confrimPasswordChange,
    loading,
    error,
    setError,
    userNotRegistered,
  };
};
